import {MarkdownRenderer} from '@github-ui/copilot-markdown'
import type {CopilotMarkdownExtension} from '@github-ui/copilot-markdown/extension'
import {useRef, useEffect, useState} from 'react'
import {clsx} from 'clsx'
import {Button} from '@primer/react'
import styles from './ExpandableMarkdown.module.css'

interface ExpandableMarkdownProps {
  markdown: string
  className?: string
  extensions?: readonly CopilotMarkdownExtension[]
  maxHeight?: number
}

export function ExpandableMarkdown({markdown, className, extensions, maxHeight = 500}: ExpandableMarkdownProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    const node = contentRef.current
    if (!node) return

    let frame = 0
    const measure = () => {
      frame = 0
      const next = node.scrollHeight > maxHeight
      setIsOverflowing(prev => (prev === next ? prev : next))
    }
    const schedule = () => {
      if (frame) return
      frame = requestAnimationFrame(measure)
    }

    schedule()

    const resizeObserver = new ResizeObserver(schedule)
    resizeObserver.observe(node)

    const mutationObserver = new MutationObserver(schedule)
    mutationObserver.observe(node, {childList: true, subtree: true, characterData: true})

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      if (frame) cancelAnimationFrame(frame)
    }
  }, [markdown, maxHeight])

  const handleToggle = () => {
    setIsExpanded(e => !e)
  }

  return (
    <div style={{'--max-height': `${maxHeight}px`}}>
      <div
        ref={contentRef}
        className={clsx(isOverflowing && styles.overflowing, isExpanded && styles.expanded)}
        // Make content non-interactive when collapsed and overflowing
        inert={isOverflowing && !isExpanded}
      >
        <MarkdownRenderer markdown={markdown} className={clsx(styles.content, className)} extensions={extensions} />
      </div>
      {isOverflowing && (
        <Button
          variant="invisible"
          size="small"
          onClick={handleToggle}
          className={styles.toggle}
          aria-expanded={isExpanded}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </div>
  )
}
